import useToken from "hooks/useToken";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { Row, Spinner } from "react-bootstrap";
import InfiniteScroll from "react-infinite-scroll-component";
import { useToast } from 'context/providers/ToastProvider';
import ComentarioItem from './ComentarioItem';
import ComentarioSkeleton from './ComentarioSkeleton';

const ComentariosSoporte = (
    {
        idClienteAkasia,
        comentarios,
        setComentarios,
        emailCliente
    }
) => {
    const [pagina, setPagina] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [cargando, setCargando] = useState(true);
    const { token, setToken } = useToken();
    const { showErrorToast, showSuccessToast } = useToast();
    const [editandoId, setEditandoId] = useState(null);
    const [textoEditado, setTextoEditado] = useState("");
    const [resolviendoId, setResolviendoId] = useState(null);
    const [textoSolucion, setTextoSolucion] = useState("");
    const [nivelesSoporte, setNivelesSoporte] = useState([]);
    const [nivelSoporteSeleccionado, setNivelSoporteSeleccionado] = useState("");
    const [minutosInvertidos, setMinutosInvertidos] = useState("");
    const [cargandoNiveles, setCargandoNiveles] = useState(false);
    const [editandoSolucionId, setEditandoSolucionId] = useState(null);
    const [textoSolucionEditado, setTextoSolucionEditado] = useState("");
    const [nivelSoporteEditado, setNivelSoporteEditado] = useState("");
    const [minutosInvertidosEditado, setMinutosInvertidosEditado] = useState("");
    const [imagenesPerfil, setImagenesPerfil] = useState({});
    const [cargandoImagenes, setCargandoImagenes] = useState({});
    const solucionTextareaRef = useRef(null);
    const minutosRef = useRef(null);
    const nivelRef = useRef(null);

    useEffect(() => {
        // Cuando cambia el soporte, reiniciar estados
        setComentarios([]);
        setPagina(1);
        setHasMore(true);
        setCargando(true);
        fetchMoreComentarios();
        obtenerNivelesSoporte();
    }, []);

    useEffect(() => {
        if (resolviendoId && solucionTextareaRef.current) {
            solucionTextareaRef.current.focus();
        }
    }, [resolviendoId]);

    /*
     * Función para obtener más comentarios
     */
    const fetchMoreComentarios = async () => {
        try {
            const response = await fetch("/api/cliente/ObtenerComentarios", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    idClienteAkasia: idClienteAkasia,
                    pagina: pagina,
                    tamanioPagina: 6,
                }),
            });

            if (response.ok) {
                const data = await response.json();
                if (data.iResponseType === 1) {
                    const parsed = JSON.parse(data.detalles);
                    const nuevosComentarios = parsed.table2;
                    console.log("Comentarios recibidos:", nuevosComentarios);

                    if (nuevosComentarios && nuevosComentarios.length > 0) {
                        // Si es la primera página, reemplazar en lugar de agregar
                        if (pagina === 1) {
                            setComentarios(nuevosComentarios);
                        } else {
                            setComentarios((prev) => [...prev, ...nuevosComentarios]);
                        }
                        setPagina((prev) => prev + 1);

                        if (nuevosComentarios.length < 6) {
                            setHasMore(false);
                        }

                        // Cargar imágenes de perfil para usuarios únicos
                        const uniqueIds = [...new Set(nuevosComentarios.map(c => c.idUsuario))];
                        uniqueIds.forEach(id => {
                            if (!imagenesPerfil[id] && !cargandoImagenes[id]) {
                                cargarImagenPerfil(id);
                            }
                        });
                    } else {
                        setHasMore(false);
                    }
                } else {
                    showErrorToast(data.vMessage);
                    setHasMore(false);
                }
            } else {
                showErrorToast(response.status + " " + response.statusText);
                if (response.status === 401) {
                    setToken(null);
                    location.href = "";
                }
                setHasMore(false);
            }
        } catch (error) {
            showErrorToast("Error inesperado al obtener comentarios");
            setHasMore(false);
        } finally {
            setCargando(false);
        }
    };

    /**
     * Inicia el modo de edición para un comentario
     */
    const iniciarEdicion = (comentario) => {
        console.log("Iniciando edición para comentario:", comentario);
        setEditandoId(comentario.idClientesAkasiaComentariosProblema);
        // Remover las etiquetas HTML si existen
        const textoSinHTML = comentario.vProblema.replace(/<[^>]*>/g, "");
        setTextoEditado(textoSinHTML);
    };

    /**
     * Cancela el modo de edición
     */
    const cancelarEdicion = () => {
        setEditandoId(null);
        setTextoEditado("");
    };

    /**
     * Guarda la edición del comentario
     */
    const guardarEdicion = async (comentario) => {
        // Validar longitud mínima
        if (textoEditado.trim().length < 15) {
            showErrorToast("El problema debe tener al menos 15 caracteres.");
            return;
        }

        console.log("Guardando comentario:", {
            idClientesAkasiaComentariosProblema: comentario.idClientesAkasiaComentariosProblema,
            vProblema: textoEditado.trim(),
        });

        try {
            const response = await fetch("/api/cliente/EditarComentarioSoporte", {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    idClientesAkasiaComentariosProblema: comentario.idClientesAkasiaComentariosProblema,
                    vProblema: textoEditado.trim(),
                }),
            });

            if (response.ok) {
                const data = await response.json();
                console.log("Respuesta del servidor:", data);

                if (data.iResponseType === 1) {
                    const comentarioActualizado = JSON.parse(data.detalles)[0];

                    setComentarios((prev) =>
                        prev.map((c) =>
                            c.idClientesAkasiaComentariosProblema === comentario.idClientesAkasiaComentariosProblema
                                ? {
                                    ...c,
                                    vProblema: comentarioActualizado.vProblema,
                                    vFecha: comentarioActualizado.vFechaActualizacion || c.vFecha,
                                    vHora: comentarioActualizado.vHoraActualizacion || c.vHora,
                                    vUpdatedUser: comentarioActualizado.vUpdatedUser,
                                }
                                : c
                        )
                    );

                    cancelarEdicion();
                    showSuccessToast("Comentario editado exitosamente");
                } else {
                    showErrorToast(data.vMessage);
                }
            } else {
                showErrorToast(response.status + " " + response.statusText);
                if (response.status === 401) {
                    setToken(null);
                    location.href = "";
                }
            }
        } catch (error) {
            showErrorToast("Error al editar el comentario: " + error);
            console.error("Error completo:", error);
        }
    };

    /**
     * Obtiene los niveles de soporte del backend
     */
    const obtenerNivelesSoporte = async () => {
        setCargandoNiveles(true);
        try {
            const response = await fetch("/api/soportetecnico/ObtenerComboBoxesRegistroSoporte", {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                console.log("Niveles de soporte recibidos:", data);

                if (data && data.table1) {
                    setNivelesSoporte(data.table1);
                }
            } else {
                console.error("Error al obtener niveles de soporte:", response.statusText);
            }
        } catch (error) {
            console.error("Error al obtener niveles de soporte:", error);
        } finally {
            setCargandoNiveles(false);
        }
    };

    /**
     * Cargar imagen del perfil del usuario
     */
    const cargarImagenPerfil = async (idUsuario) => {
        if (!idUsuario) return;

        setCargandoImagenes(prev => ({ ...prev, [idUsuario]: true }));

        try {
            const response = await fetch(`/api/perfil/obtenerImagenPerfil/?idUsuario=${idUsuario}`, {
                method: 'GET',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'text/plain'
                }
            });

            if (response.ok) {
                const data = await response.text();
                setImagenesPerfil(prev => ({ ...prev, [idUsuario]: data }));
            } else {
                console.error('Error al cargar imagen:', response.status);
                setImagenesPerfil(prev => ({ ...prev, [idUsuario]: '' }));
            }
        } catch (error) {
            console.error('Error al cargar imagen del perfil:', error);
            setImagenesPerfil(prev => ({ ...prev, [idUsuario]: '' }));
        } finally {
            setCargandoImagenes(prev => ({ ...prev, [idUsuario]: false }));
        }
    };

    /**
     * Inicia el modo de resolución para un comentario
     */
    const iniciarResolucion = async (comentario) => {
        console.log("Iniciando resolución para comentario:", comentario);
        setResolviendoId(comentario.idClientesAkasiaComentariosProblema);
        setTextoSolucion("");
        setNivelSoporteSeleccionado("");
        setMinutosInvertidos("");

        if (nivelesSoporte.length === 0) {
            await obtenerNivelesSoporte();
        }
    };

    /**
     * Cancela el modo de resolución
     */
    const cancelarResolucion = () => {
        setResolviendoId(null);
        setTextoSolucion("");
        setNivelSoporteSeleccionado("");
        setMinutosInvertidos("");
    };

    /**
     * Guarda la resolución del comentario
     */
    const guardarResolucion = async (comentario) => {
        if (textoSolucion.trim().length < 15) {
            showErrorToast("La solución debe tener al menos 15 caracteres.");
            return;
        }

        if (!minutosInvertidos || minutosInvertidos === "" || parseInt(minutosInvertidos) <= 0) {
            showErrorToast("Debe ingresar los minutos invertidos (mayor a 0).");
            minutosRef.current?.focus();
            return;
        }

        if (!nivelSoporteSeleccionado || nivelSoporteSeleccionado === "") {
            showErrorToast("Debe seleccionar un nivel de soporte.");
            nivelRef.current?.focus();
            return;
        }

        try {
            const response = await fetch("/api/cliente/ResolverComentarioSoporte", {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    idClientesAkasiaComentariosProblema: comentario.idClientesAkasiaComentariosProblema,
                    vSolucion: textoSolucion.trim(),
                    dTiempo: parseInt(minutosInvertidos),
                    idCatNivelSoporte: nivelSoporteSeleccionado,
                }),
            });

            if (response.ok) {
                const data = await response.json();
                console.log("Respuesta del servidor:", data);

                if (data.iResponseType === 1) {
                    const comentarioActualizado = JSON.parse(data.detalles)[0];

                    setComentarios((prev) =>
                        prev.map((c) =>
                            c.idClientesAkasiaComentariosProblema === comentario.idClientesAkasiaComentariosProblema
                                ? {
                                    ...c,
                                    vSolucion: comentarioActualizado.vSolucion,
                                    bResuelto: true,
                                    vFechaSolucion: comentarioActualizado.vFechaSolucion,
                                    vHoraSolucion: comentarioActualizado.vHoraSolucion,
                                    vUsuarioSolucion: comentarioActualizado.vUsuarioSolucion,
                                    idUsuarioSolucion: comentarioActualizado.idUsuarioSolucion,
                                    dTiempo: comentarioActualizado.dTiempo,
                                    vTiempoSolucionTexto: comentarioActualizado.vTiempoSolucionTexto,
                                    iMinutosParaResolver: comentarioActualizado.iMinutosParaResolver,
                                    idCatNivelSoporte: comentarioActualizado.idCatNivelSoporte,
                                    vNombreNivel: comentarioActualizado.vNombreNivel,
                                    iNivel: comentarioActualizado.iNivel,
                                    vEstado: comentarioActualizado.vEstado || "Resuelto",
                                }
                                : c
                        )
                    );

                    cancelarResolucion();
                    showSuccessToast("Problema resuelto exitosamente");
                } else {
                    showErrorToast(data.vMessage);
                }
            } else {
                showErrorToast(response.status + " " + response.statusText);
                if (response.status === 401) {
                    setToken(null);
                    location.href = "";
                }
            }
        } catch (error) {
            showErrorToast("Error al resolver el comentario: " + error);
            console.error("Error completo:", error);
        }
    };

    /**
     * Inicia el modo de edición de solución para un comentario resuelto
     */
    const iniciarEdicionSolucion = async (comentario) => {
        console.log("Iniciando edición de solución para comentario:", comentario);
        setEditandoSolucionId(comentario.idClientesAkasiaComentariosProblema);
        const textoSinHTML = (comentario.vSolucion || "").replace(/<[^>]*>/g, "");
        setTextoSolucionEditado(textoSinHTML);
        setNivelSoporteEditado(comentario.idCatNivelSoporte || "");
        setMinutosInvertidosEditado(comentario.dTiempo || "");

        if (nivelesSoporte.length === 0) {
            await obtenerNivelesSoporte();
        }
    };

    /**
     * Cancela el modo de edición de solución
     */
    const cancelarEdicionSolucion = () => {
        setEditandoSolucionId(null);
        setTextoSolucionEditado("");
        setNivelSoporteEditado("");
        setMinutosInvertidosEditado("");
    };

    /**
     * Guarda la edición de la solución
     */
    const guardarEdicionSolucion = async (comentario) => {
        if (textoSolucionEditado.trim().length < 15) {
            showErrorToast("La solución debe tener al menos 15 caracteres.");
            return;
        }

        if (!minutosInvertidosEditado || minutosInvertidosEditado === "" || parseInt(minutosInvertidosEditado) <= 0) {
            showErrorToast("Debe ingresar los minutos invertidos (mayor a 0).");
            minutosRef.current?.focus();
            return;
        }

        if (!nivelSoporteEditado || nivelSoporteEditado === "") {
            showErrorToast("Debe seleccionar un nivel de soporte.");
            return;
        }

        try {
            const response = await fetch("/api/cliente/EditarSolucionProblemaSoporte", {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    idClientesAkasiaComentariosProblema: comentario.idClientesAkasiaComentariosProblema,
                    vSolucion: textoSolucionEditado.trim(),
                    dTiempo: parseInt(minutosInvertidosEditado),
                    idCatNivelSoporte: nivelSoporteEditado,
                }),
            });

            if (response.ok) {
                const data = await response.json();
                console.log("Respuesta del servidor:", data);

                if (data.iResponseType === 1) {
                    const comentarioActualizado = JSON.parse(data.detalles)[0];

                    setComentarios((prev) =>
                        prev.map((c) =>
                            c.idClientesAkasiaComentariosProblema === comentario.idClientesAkasiaComentariosProblema
                                ? {
                                    ...c,
                                    vSolucion: comentarioActualizado.vSolucion,
                                    bResuelto: true,
                                    vFechaSolucion: comentarioActualizado.vFechaSolucion,
                                    vHoraSolucion: comentarioActualizado.vHoraSolucion,
                                    vUsuarioSolucion: comentarioActualizado.vUsuarioSolucion,
                                    idUsuarioSolucion: comentarioActualizado.idUsuarioSolucion,
                                    dTiempo: comentarioActualizado.dTiempo,
                                    vTiempoSolucionTexto: comentarioActualizado.vTiempoSolucionTexto,
                                    vTiempoTexto: comentarioActualizado.vTiempoTexto,
                                    iMinutosParaResolver: comentarioActualizado.iMinutosParaResolver,
                                    idCatNivelSoporte: comentarioActualizado.idCatNivelSoporte,
                                    vNombreNivel: comentarioActualizado.vNombreNivel,
                                    iNivel: comentarioActualizado.iNivel,
                                    vEstado: comentarioActualizado.vEstado || "Resuelto",
                                }
                                : c
                        )
                    );

                    cancelarEdicionSolucion();
                    showSuccessToast("Solución actualizada exitosamente");
                } else {
                    showErrorToast(data.vMessage);
                }
            } else {
                showErrorToast(response.status + " " + response.statusText);
                if (response.status === 401) {
                    setToken(null);
                    location.href = "";
                }
            }
        } catch (error) {
            showErrorToast("Error al editar la solución: " + error);
            console.error("Error completo:", error);
        }
    };

    return (

        cargando ? (
            <ComentarioSkeleton />
        ) : (
            <InfiniteScroll
                dataLength={comentarios.length}
                next={fetchMoreComentarios}
                hasMore={hasMore}
                loader={
                    <div className="d-flex justify-content-center align-items-center py-3">
                        <Spinner animation="border" size="sm" variant="primary" className="me-2" />
                        <span className="text-muted" style={{ fontSize: "0.85rem" }}>
                            Cargando m&aacute;s comentarios...
                        </span>
                    </div>
                }
                scrollableTarget="scrollableTargetComentariosSoporte"
                scrollThreshold={0.95}
                style={{ overflowX: "hidden" }}
                endMessage={
                    comentarios.length > 0 && (
                        <div className="text-center py-3 border-top">
                            <small className="text-muted" style={{ fontSize: "0.8rem" }}>
                                Todos los comentarios cargados
                            </small>
                        </div>
                    )
                }
            >
                {comentarios.length === 0 ? (
                    <div className="text-center py-5">
                        <span className="text-muted mb-3" style={{ fontSize: 40 }}>
                            🗨️
                        </span>
                        <h6 className="text-muted mb-2">Sin comentarios</h6>
                        <p className="text-muted mb-0" style={{ fontSize: "0.9rem" }}>
                            No hay comentarios registrados para mostrar
                        </p>
                    </div>
                ) : (
                    <Row className="g-2 card">
                        {comentarios.map((comentario, index) => (
                            <ComentarioItem
                                key={`${comentario.idClientesAkasiaComentariosProblema}-${index}`}
                                comentario={comentario}
                                editandoId={editandoId}
                                resolviendoId={resolviendoId}
                                editandoSolucionId={editandoSolucionId}
                                textoEditado={textoEditado}
                                setTextoEditado={setTextoEditado}
                                textoSolucion={textoSolucion}
                                setTextoSolucion={setTextoSolucion}
                                textoSolucionEditado={textoSolucionEditado}
                                setTextoSolucionEditado={setTextoSolucionEditado}
                                nivelSoporteSeleccionado={nivelSoporteSeleccionado}
                                setNivelSoporteSeleccionado={setNivelSoporteSeleccionado}
                                minutosInvertidos={minutosInvertidos}
                                setMinutosInvertidos={setMinutosInvertidos}
                                nivelSoporteEditado={nivelSoporteEditado}
                                setNivelSoporteEditado={setNivelSoporteEditado}
                                minutosInvertidosEditado={minutosInvertidosEditado}
                                setMinutosInvertidosEditado={setMinutosInvertidosEditado}
                                nivelesSoporte={nivelesSoporte}
                                cargandoNiveles={cargandoNiveles}
                                imagenesPerfil={imagenesPerfil}
                                cargandoImagenes={cargandoImagenes}
                                solucionTextareaRef={solucionTextareaRef}
                                minutosRef={minutosRef}
                                nivelRef={nivelRef}
                                iniciarEdicion={iniciarEdicion}
                                cancelarEdicion={cancelarEdicion}
                                guardarEdicion={guardarEdicion}
                                iniciarResolucion={iniciarResolucion}
                                cancelarResolucion={cancelarResolucion}
                                guardarResolucion={guardarResolucion}
                                iniciarEdicionSolucion={iniciarEdicionSolucion}
                                cancelarEdicionSolucion={cancelarEdicionSolucion}
                                guardarEdicionSolucion={guardarEdicionSolucion}
                                emailCliente={emailCliente}
                                setComentarios={setComentarios}
                            />
                        ))}
                    </Row>
                )}
            </InfiniteScroll>
        )

    );
}

ComentariosSoporte.propTypes = {
    idClienteAkasia: PropTypes.string,
    comentarios: PropTypes.array,
    setComentarios: PropTypes.func,
    emailCliente: PropTypes.string
};

export default ComentariosSoporte;
